import React, { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { MapContainer, Marker, Polygon, Popup, TileLayer } from "react-leaflet"
import { divIcon } from "leaflet"
import { useHistory, useParams } from "react-router-dom"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import Pagination from "@/components/core/Pagination"
import { Routes } from "@/routes"

interface GeofenceCoordinate {
  latitude: number
  longitude: number
}

interface GeofenceDetailsResponse {
  id: string
  organizationId: string
  name: string
  description?: string
  placeId?: string
  geometryJson: string
  sourceFileName?: string
  sourceType?: string
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
  centroidLatitude?: number
  centroidLongitude?: number
  created: string
  updated?: string
  place?: {
    id: string
    name: string
    address?: string
    latitude?: number
    longitude?: number
  }
}

interface GeofenceStop {
  id: number
  startDate: string
  endDate: string | null
  durationMinutes: number | null
  latitude: number | null
  longitude: number | null
  placeMatchDistanceM: number | null
  vehicleName: string | null
  externalVehicleId: number
  placeId: string | null
  placeName: string | null
}

const placePinIcon = divIcon({
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#0d6efd;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);"></div>`,
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
})

const GeofenceDetails: React.FC = () => {
  const { geofenceId } = useParams<{ geofenceId: string }>()
  const { t } = useTranslation("global")
  const history = useHistory()
  const user = parsedUser()
  const [model, setModel] = useState<GeofenceDetailsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [stops, setStops] = useState<GeofenceStop[]>([])
  const [isLoadingStops, setIsLoadingStops] = useState(false)
  const [stopsError, setStopsError] = useState("")
  const [stopsPage, setStopsPage] = useState(0)
  const [stopsTotalPages, setStopsTotalPages] = useState(0)
  const [stopsTotalRecords, setStopsTotalRecords] = useState(0)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isBackfilling, setIsBackfilling] = useState(false)
  const [backfillResult, setBackfillResult] = useState<{ updatedStops: number } | null>(null)

  const coordinates = useMemo(() => {
    if (!model?.geometryJson) {
      return [] as GeofenceCoordinate[]
    }

    try {
      const parsed = JSON.parse(model.geometryJson)
      if (!Array.isArray(parsed)) {
        return []
      }

      return parsed.filter(
        (item): item is GeofenceCoordinate =>
          typeof item?.latitude === "number" &&
          typeof item?.longitude === "number"
      )
    } catch {
      return []
    }
  }, [model])

  const polygonPositions = coordinates.map(item => [item.latitude, item.longitude] as [number, number])
  const polygonCenter =
    model?.centroidLatitude && model?.centroidLongitude
      ? [model.centroidLatitude, model.centroidLongitude] as [number, number]
      : polygonPositions[0] || [18.479777, -69.9341112]

  useEffect(() => {
    const request = async () => {
      if (!geofenceId || !user?.OrganizationId) {
        return
      }

      try {
        setIsLoading(true)
        setError("")
        const query = `Id=${geofenceId}&OrganizationId=${user.OrganizationId}`
        const response = await API.getAction("Geofences/Details", query)

        if (response.data.status === "ok") {
          setModel(response.data.response)
          return
        }

        setError(response.data.response || t("Unable to load geofence"))
      } catch (requestError: unknown) {
        const apiError = requestError as {
          response?: { data?: { response?: string } }
          message?: string
        }

        setError(
          apiError?.response?.data?.response ||
          apiError?.message ||
          t("Unable to load geofence")
        )
      } finally {
        setIsLoading(false)
      }
    }

    request()
  }, [geofenceId, t, user?.OrganizationId])

  useEffect(() => {
    setStopsPage(0)
  }, [geofenceId])

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setStopsPage(0)
      setDebouncedSearch(value)
    }, 350)
  }

  const runBackfill = async () => {
    if (!user?.OrganizationId) return
    try {
      setIsBackfilling(true)
      setBackfillResult(null)
      const response = await API.postAction("Geofences/BackfillStops", { organizationId: user.OrganizationId })
      if (response.data.status === "ok") {
        setBackfillResult(response.data.response)
        setStopsPage(0)
        setDebouncedSearch("")
        setSearch("")
      }
    } catch {
      // silent — user sees no result
    } finally {
      setIsBackfilling(false)
    }
  }

  useEffect(() => {
    const requestStops = async () => {
      if (!geofenceId || !user?.OrganizationId) {
        setStops([])
        setStopsTotalPages(0)
        setStopsTotalRecords(0)
        setStopsError("")
        return
      }

      try {
        setIsLoadingStops(true)
        setStopsError("")
        const params = [
          `Id=${geofenceId}`,
          `OrganizationId=${user.OrganizationId}`,
          `Page=${stopsPage}`,
          `Quantity=20`,
          debouncedSearch ? `Search=${encodeURIComponent(debouncedSearch)}` : "",
        ].filter(Boolean).join("&")

        const response = await API.getAction("Geofences/Stops", params)

        if (response.data.status === "ok") {
          setStops((response.data.response || []) as GeofenceStop[])
          setStopsTotalPages(response.data.quantityOfPages || 0)
          setStopsTotalRecords(response.data.quantityOfRecords || 0)
          return
        }

        setStopsError(response.data.response || t("Unable to load geofence stops"))
      } catch (requestError: unknown) {
        const apiError = requestError as {
          response?: { data?: { response?: string } }
          message?: string
        }

        setStopsError(
          apiError?.response?.data?.response ||
          apiError?.message ||
          t("Unable to load geofence stops")
        )
      } finally {
        setIsLoadingStops(false)
      }
    }

    requestStops()
  }, [geofenceId, t, user?.OrganizationId, stopsPage, debouncedSearch])

  return (
    <div className="px-3 px-lg-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h3 className="mb-1">{t("Geofence Details")}</h3>
          <small className="text-muted">{t("Imported geofence information and map")}</small>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => history.push(Routes.Home.path)}>
            {t("Back to map")}
          </Button>
          {model?.place?.id && (
            <Button variant="primary" onClick={() => history.push(Routes.PlaceDetails.clean + model.place?.id)}>
              {t("Open linked place")}
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="d-flex align-items-center gap-2 py-4">
          <Spinner animation="border" size="sm" />
          <span>{t("Loading geofence...")}</span>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!isLoading && !error && model && (
        <>
          <Row className="g-3">
            <Col lg={8}>
              <Card className="shadow-sm border-0">
                <Card.Body style={{ minHeight: "460px" }}>
                  <MapContainer
                    center={polygonCenter}
                    zoom={16}
                    style={{ width: "100%", height: "430px", borderRadius: "12px" }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {polygonPositions.length > 0 && (
                      <>
                        <Polygon
                          positions={polygonPositions}
                          pathOptions={{
                            color: "#ffffff",
                            weight: 8,
                            opacity: 0.95,
                            fillOpacity: 0,
                          }}
                        />
                        <Polygon
                          positions={polygonPositions}
                          pathOptions={{
                            color: "#ff7a00",
                            weight: 4,
                            fillColor: "#ff9f43",
                            fillOpacity: 0.24,
                          }}
                        >
                          <Popup>{model.name}</Popup>
                        </Polygon>
                      </>
                    )}

                    {model.place?.latitude && model.place?.longitude && (
                      <Marker
                        position={[model.place.latitude, model.place.longitude]}
                        icon={placePinIcon}
                      >
                        <Popup>{model.place.name}</Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body>
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <div>
                      <h4 className="mb-1">{model.name}</h4>
                      <div className="text-muted small">{model.id}</div>
                    </div>
                    <Badge bg={model.placeId ? "success" : "secondary"}>
                      {model.placeId ? t("Linked to place") : t("Without linked place")}
                    </Badge>
                  </div>

                  {model.description && (
                    <p className="text-muted mb-3">{model.description}</p>
                  )}

                  <div className="small">
                    <div className="mb-2">
                      <strong>{t("Vertices")}:</strong> {coordinates.length}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Source file")}:</strong> {model.sourceFileName || "-"}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Source type")}:</strong> {model.sourceType || "-"}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Vehicle Stops")}:</strong> {stopsTotalRecords.toLocaleString()}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Created")}:</strong> {new Date(model.created).toLocaleString()}
                    </div>
                    {model.updated && (
                      <div className="mb-2">
                        <strong>{t("Updated")}:</strong> {new Date(model.updated).toLocaleString()}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0 mb-3">
                <Card.Body>
                  <h5 className="mb-3">{t("Linked place")}</h5>
                  {model.place ? (
                    <>
                      <div className="fw-semibold">{model.place.name}</div>
                      <div className="text-muted small mb-3">{model.place.address || "-"}</div>
                      <Button
                        variant="outline-primary"
                        onClick={() => history.push(Routes.PlaceDetails.clean + model.place?.id)}
                      >
                        {t("Open linked place")}
                      </Button>
                    </>
                  ) : (
                    <div className="text-muted">{t("This geofence is not linked to a place yet")}</div>
                  )}
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0">
                <Card.Body>
                  <h5 className="mb-3">{t("Bounds")}</h5>
                  <div className="small">
                    <div className="mb-2"><strong>{t("Min latitude")}:</strong> {model.minLatitude}</div>
                    <div className="mb-2"><strong>{t("Max latitude")}:</strong> {model.maxLatitude}</div>
                    <div className="mb-2"><strong>{t("Min longitude")}:</strong> {model.minLongitude}</div>
                    <div className="mb-2"><strong>{t("Max longitude")}:</strong> {model.maxLongitude}</div>
                    <div className="mb-2"><strong>{t("Centroid")}:</strong> {model.centroidLatitude ?? "-"}, {model.centroidLongitude ?? "-"}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm border-0 mt-3">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{t("Vehicle Stops")}</h5>
                  <Badge bg="light" text="dark">{stopsTotalRecords.toLocaleString()}</Badge>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div style={{ minWidth: "260px" }}>
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        placeholder={t("Search by vehicle or place...")}
                        value={search}
                        onChange={e => handleSearchChange(e.target.value)}
                      />
                      {search && (
                        <Button variant="outline-secondary" onClick={() => handleSearchChange("")}>
                          ✕
                        </Button>
                      )}
                    </InputGroup>
                  </div>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={runBackfill}
                    disabled={isBackfilling}
                    title={t("Match historical stops against geofences")}
                  >
                    {isBackfilling ? (
                      <><Spinner animation="border" size="sm" className="me-1" />{t("Running backfill...")}</>
                    ) : (
                      t("Run backfill")
                    )}
                  </Button>
                  {backfillResult && (
                    <Badge bg="success">
                      {backfillResult.updatedStops.toLocaleString()} {t("stops matched")}
                    </Badge>
                  )}
                </div>
              </div>

              {stopsError && <Alert variant="danger">{stopsError}</Alert>}

              {isLoadingStops ? (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <thead>
                        <tr>
                          <th>{t("Entry")}</th>
                          <th>{t("Exit")}</th>
                          <th className="text-end">{t("Duration")}</th>
                          <th>{t("Vehicle")}</th>
                          <th>{t("Place")}</th>
                          <th className="text-end">{t("Distance (mts)")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stops.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3">
                              {t("No records found")}
                            </td>
                          </tr>
                        ) : stops.map(stop => (
                          <tr key={stop.id}>
                            <td className="text-nowrap">{new Date(stop.startDate).toLocaleString()}</td>
                            <td className="text-nowrap">
                              {stop.endDate ? new Date(stop.endDate).toLocaleString() : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-end text-nowrap">
                              {stop.durationMinutes != null ? `${stop.durationMinutes} min` : "-"}
                            </td>
                            <td>{stop.vehicleName ?? stop.externalVehicleId}</td>
                            <td>
                              {stop.placeId ? (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0"
                                  onClick={() => history.push(Routes.PlaceDetails.clean + stop.placeId)}
                                >
                                  {stop.placeName ?? stop.placeId}
                                </button>
                              ) : (
                                stop.placeName || "-"
                              )}
                            </td>
                            <td className="text-end">
                              {stop.placeMatchDistanceM != null ? `${stop.placeMatchDistanceM} m` : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {stopsTotalPages > 1 && (
                    <div className="mt-3">
                      <Pagination
                        totalPages={stopsTotalPages}
                        currentPage={stopsPage}
                        totalRecords={stopsTotalRecords}
                        onPageChanged={setStopsPage}
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  )
}

export default GeofenceDetails
